import { useEffect, useRef, useState, useCallback } from 'react';
import { useMemoizedFn } from 'ahooks';
import { getCookie, VISITOR_ID_COOKIE_KEY } from '@/utils/auth';
import useUserStore from '@/stores/userStore';
import { webSocketService } from '../services/websocket';
import type { SubscribeId, MessageHandler, ConnectionState, WebSocketMessage } from '../services/websocket';
import { PushEventType } from '../services/websocket/BDSocketManager';
// import { useStore } from '@/stores/appStore';
// 原样导出 PushEventType，以便在组件中使用
export { PushEventType };

/**
 * WebSocket Hook选项
 */
export interface UseWebSocketOptions {
  /** 自定义设备ID，不传则使用当前用户email */
  deviceId?: string;
  /** 是否自动连接 */
  autoConnect?: boolean;
}

/**
 * WebSocket Hook返回值
 */
export interface UseWebSocketReturn {
  /** 连接状态 */
  connectionState: ConnectionState;
  /** 连接WebSocket */
  connect: () => Promise<void>;
  /** 添加消息订阅 */
  subscribe: <T = string>(callback: MessageHandler<T>) => SubscribeId;
  /** 移除消息订阅 */
  unsubscribe: (subscribeId: SubscribeId) => void;
}

/**
 * WebSocket React Hook
 * 职责：将 WebSocket 服务绑定到 React 组件生命周期
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const { autoConnect = false } = options;

  // 已登录用户用 userId，未登录用 visitor_id；优先 options.deviceId
  const user = useUserStore(s => s.user);
  const deviceId = options.deviceId ?? user?.userId ?? getCookie(VISITOR_ID_COOKIE_KEY) ?? '';

  const [connectionState, setConnectionState] = useState<ConnectionState>({
    isConnected: false,
    isConnecting: false,
    error: null,
  });

  // 集中管理订阅ID
  const subscriptionIdsRef = useRef<Set<SubscribeId>>(new Set());

  // 初始化服务
  useEffect(() => {
    if (deviceId) {
      webSocketService.initialize(deviceId);
    }
  }, [deviceId]);

  // 监听连接状态变化
  useEffect(() => {
    const handleStateChange = (state: unknown) => {
      setConnectionState(state as ConnectionState);
    };

    webSocketService.on('state_change', handleStateChange);

    // 获取初始状态
    setConnectionState(webSocketService.getConnectionState());

    return () => {
      webSocketService.off('state_change', handleStateChange);
    };
  }, []);

  // 自动连接
  useEffect(() => {
    if (autoConnect && deviceId) {
      webSocketService.connect().catch(error => {
        console.error('自动连接失败:', error);
      });
    }
  }, [autoConnect, deviceId]);

  // 组件卸载时清理所有订阅
  useEffect(() => {
    const subscriptionIds = subscriptionIdsRef.current;
    return () => {
      // 清理所有订阅
      subscriptionIds.forEach(id => {
        webSocketService.removeMessageSubscription(id);
      });
      subscriptionIds.clear();
      console.log('useWebSocket: All message subscriptions cleared');
    };
  }, []);

  // 连接方法
  const connect = useCallback(async () => {
    await webSocketService.connect();
  }, []);

  // 订阅消息（自动添加到集中管理）
  const subscribe = useCallback(<T = string>(callback: MessageHandler<T>): SubscribeId => {
    const id = webSocketService.addMessageSubscription(callback);
    subscriptionIdsRef.current.add(id);
    return id;
  }, []);

  // 取消订阅（从集中管理中移除）
  const unsubscribe = useCallback((subscribeId: SubscribeId) => {
    webSocketService.removeMessageSubscription(subscribeId);
    subscriptionIdsRef.current.delete(subscribeId);
  }, []);

  return {
    connectionState,
    connect,
    subscribe,
    unsubscribe,
  };
}

/**
 * WebSocket消息订阅Hook
 * 自动管理订阅的生命周期
 */
export function useALLPushMessage<T = string>(
  messageHandler: MessageHandler<T>,
  options: UseWebSocketOptions = {}
): ConnectionState {
  const { connectionState, subscribe, unsubscribe } = useWebSocket({
    autoConnect: true,
    ...options,
  });

  const subscribeIdRef = useRef<SubscribeId | null>(null);

  // 使用 useMemoizedFn 保持 handler 引用稳定
  const memoizedHandler = useMemoizedFn(messageHandler);

  // 订阅消息
  useEffect(() => {
    subscribeIdRef.current = subscribe(memoizedHandler);

    return () => {
      if (subscribeIdRef.current) {
        unsubscribe(subscribeIdRef.current);
        subscribeIdRef.current = null;
      }
    };
  }, [subscribe, unsubscribe, memoizedHandler]);

  return connectionState;
}

/**
 * WebSocket消息订阅Hook - 根据消息类型订阅
 * @param eventType 要订阅的消息类型
 * @param messageHandler 消息处理函数
 * @param options WebSocket选项
 * @returns 连接状态
 */
export function usePushMessage<T = string>(
  eventType: PushEventType,
  messageHandler: MessageHandler<T>,
  options: UseWebSocketOptions = {}
): ConnectionState {
  const { connectionState, subscribe, unsubscribe } = useWebSocket({
    autoConnect: true,
    ...options,
  });

  const subscribeIdRef = useRef<SubscribeId | null>(null);

  // 使用 useMemoizedFn 保持 handler 引用稳定
  const memoizedHandler = useMemoizedFn((message: WebSocketMessage<T>) => {
    // 只有当消息类型匹配时才触发回调
    if (message.eventType === eventType) {
      messageHandler(message);
    }
  });

  // 订阅消息
  useEffect(() => {
    subscribeIdRef.current = subscribe<T>(memoizedHandler);

    return () => {
      if (subscribeIdRef.current) {
        unsubscribe(subscribeIdRef.current);
        subscribeIdRef.current = null;
      }
    };
  }, [subscribe, unsubscribe, memoizedHandler]);

  return connectionState;
}
